<template>
    <b-overlay :show="!isBudgetSignValid || isMakingPageToPdf" rounded="sm" :opacity="1" no-fade>
       
        <div class="doc-info">
            <div class="doc-info_header">
                <a class="doc-info_change-to-print" href="/"><img src="img/logo-full.svg" alt="logo full"></a>
                <div 
                    class="doc-info_lang-switch doc-info_change-to-print" 
                    @click="onSwitchLangClicked"
                >
                    <span>{{ isLangPanelWrapped ? locale : '...' }} </span>
                </div>
                <div class="doc-info_lang-group doc-info_change-to-print" :class="{wrapped: isLangPanelWrapped}">
                    <template v-for="(listLocale, i) in locales">
                        <b-list-group-item
                            :key="'locale-button-' + i"
                            class="doc-info_lang-item"
                            :class="[
                                `doc-info_lang-item_${listLocale}`,
                                { 
                                    active: listLocale === locale,
                                    wrapped: !isLangPanelWrapped
                                }
                            ]"
                        >
                            <span
                                class="doc-info_lang"
                                :class="{'active': listLocale === locale}"
                                @click="setLocale(listLocale)"
                            >    {{ listLocale }}</span>
                        </b-list-group-item>
                    </template>
                </div>
            </div>

            <div class="doc-inf_container doc-info_change-to-print">
                <p class="doc-inf_title doc-info_change-to-print">{{ getLocText('title') }}</p>
                <div class="doc-inf_wrapper">
                    <p class="doc-inf_section-title">{{ `${getLocText('info_sign')}:` }}</p>
                    <div class="doc-inf_section" :class="{'error': isSignExpired}"  v-for="signer in signers" :key="signer.user_id">
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('name_sign') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{  `${signer.last_name} ${signer.first_name} ${signer.middle_name}` }}</p>
                        </div>
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('position') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{ signer.pos_short_name_ru }}</p>
                        </div>
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print" :class="{'error': isSignExpired}">{{ getLocText('validity_date') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print" :class="{'error': isSignExpired}">
                                {{ formatIsoToDateTime(signer.final_date) }}
                                <span v-if="isSignExpired"> {{ getLocText('sign_expired') }} </span>
                            </p>
                        </div>
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('sign_date') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{ formatIsoToDateTime(signer.update_date) }}</p>
                        </div>
                    </div>

                    <p class="doc-inf_section-title">{{ `${getLocText('request_info')}:` }}</p>
                    <div class="doc-inf_section">
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('doc_type') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{ documentType }}</p>
                        </div>
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('plan_period') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{ planPeriod }}</p>
                        </div>
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('region') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{ regionName }}</p>
                        </div>
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('level') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{ levelName }}</p>
                        </div>
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('gu_code') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{ guName }}</p>
                        </div>
                        <div class="doc-info_row doc-info_change-to-print">
                            <p class="doc-info_left-col doc-info_change-to-print">{{ getLocText('variant') }}</p>
                            <p class="doc-info_right-col doc-info_change-to-print">{{ variantName }}</p>
                        </div>
                    </div>

                    <p class="doc-inf_section-title doc-info_change-to-print">{{ `${getLocText('doc_link')}:` }}</p>
                    <div class="doc-inf_section doc-info_change-to-print">
                        <div v-for="(year, idx) of years" :key="idx" class="doc-info_row links">
                            <div class="doc-info_link-wrapper">
                                <p class="doc-info_left-col">{{ year }}</p>
                                <p class="doc-info_link" @click="downloadFile(year)">{{ getLocText('follow_link') }}</p>
                            </div>
                            
                            <div class="doc-info_button" @click="copyToClipboard(year)">
                                <img 
                                    class="doc-info_copy-icon" 
                                    src="img/svg-icons/copy.svg" 
                                    alt="copy icon"
                                >
                                {{ getLocText('copy_link') }}
                            </div>
                        </div>
                    </div>

                    <div class="doc-info_btn-wrapper doc-info_change-to-print">
                        <b-button 
                            class="doc-info_footer-wrapper"
                            variant="success"
                            @click="downloadPageData"
                        >
                            <i class="icon icon-download"></i>
                            {{ getLocText('download_data') }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
        <template #overlay>
          <p v-if="!isBudgetSignValid" id="cancel-label">{{ getLocText('doc_not_valid') }}</p>
          <b-spinner v-if="isMakingPageToPdf" label="Spinning"></b-spinner>
        </template>
    </b-overlay>
</template>

<script>
    import i18nService from "@/services/i18n";
    import html2pdf from 'html2pdf.js';

    export default {
    name: 'document-info',
    components: { 
    },

    beforeCreate() {
        const mainContainer = document.querySelector('.main-container');
        mainContainer.style.margin = '0px';
        mainContainer.style.height = '100dvh';
    },

    mounted() {
        this.onMounted();
    },

    data() {
        return {
            isSignExpired: false,
            documentData: [
                {year: 2022, link: 'sfgdfgdf'},
                {year: 2023, link: 'sfgdfgdf'},
                {year: 2024, link: 'sfgdfgdf'}
            ],
            requiredParams: ['abp', 'gu', 'level', 'mode', 'variant'],
            params: {},
            modeWithHash: ['brft', 'bip_br'],
            isBudgetSignValid: true,
            signers: [],
            region: {},
            guDict: {},
            dictLevels: {
                '1': {name_ru: 'УО', name_kk: 'УО(каз)'},
                '2': {name_ru: 'АБП', name_kk: 'АБП(каз)'},
                '3': {name_ru: 'ГУ', name_kk: 'ГУ(каз)'}
            }, 
            locale: i18nService.locale,
            isLangPanelWrapped: true,
            isBtnDisabled: false,
            isOverlayShowed: false,
            isMakingPageToPdf: false
        }
    },
    methods: {
        getLocText(prop) {
            const path = `document_info.${prop}`;
            return this.$t(path);
        },

        setLocale(value) {
            this.isLangPanelWrapped = true;
            this.locale = value;
            i18nService.locale = value;
        },

        onMounted() {
            const areAllParamsValid = this.getUrlParams();
            if (!areAllParamsValid) return;
            this.hashValidation();
            this.isOverlayShowed = !this.isBudgetSignValid;
            if (!this.isBudgetSignValid) return;
            this.getRGuData();
            this.getSignersData();
        },

        getUrlParams() {
            const urlParams = this.$route.query;
            const missingParams = [];

            for (const par of this.requiredParams) {
                if (urlParams[par] === undefined) {
                    missingParams.push(par);
                } 
            }
            if (missingParams.length) {
                this.makeToast('danger', 'Url Error', `Params "${missingParams.join('", "')}" were lost`);
                return false;
            } else {
                this.$set(this.params, 'abp', this.parseQueryParam(this.$route.query.abp, 'number'));
                this.$set(this.params, 'gu', this.parseQueryParam(this.$route.query.gu, 'string'));
                this.$set(this.params, 'level', this.parseQueryParam(this.$route.query.level, 'number'));
                this.$set(this.params, 'mode', this.parseQueryParam(this.$route.query.mode, 'obj'));
                this.$set(this.params, 'variant', this.parseQueryParam(this.$route.query.variant, 'string'));
                
                return true;
            }
        },

        async hashValidation() {
            const modes = this.params.mode;
            const modesToValidate = {};

            let isHasModesWithHash = false;
            this.modeWithHash.forEach(mode => {
                if (mode in modes && modes[mode] !== 'None') {
                    isHasModesWithHash = true;
                    modesToValidate[mode] = modes[mode];
                }
            })

            if (!isHasModesWithHash) return true;

            const data = {
                abp: this.params.abp,
                gu: this.params.level === 3 ? this.params.gu : false,
                variant: this.params.variant,
                modes: JSON.stringify(modesToValidate)
            }

            try {
                const queryString = new URLSearchParams(data).toString();
                const url = `/api-py/budget-sign-validation?${queryString}`;
                const response = await fetch(url);
                if (response.ok) {
                    this.isBudgetSignValid = await response.json();
                } else {
                    this.makeToast('danger', 'Error', 'Budget sign validation error');
                    return false;
                }
            } catch {
                this.makeToast('danger', 'Error', 'Budget sign validation error');
                return false;
            }
        },

        async getSignersData() {
            const data = {
                abp: this.params.abp,
                gu: this.params.gu,
                variant: this.params.variant,
                level: this.params.level
            }

            try {
                const queryString = new URLSearchParams(data).toString();
                const url = `/api-py/get-signers-data?${queryString}`;
                const response = await fetch(url);
                if (response.ok) {
                    const result = await response.json();
                    this.signers = result;
                    this.getRegionData();
                } else {
                    this.makeToast('danger', 'Error', 'Error while getting signers data');
                }
            } catch {
                this.makeToast('danger', 'Error', 'Error while getting signers data');
            }
        },
        
        async getRegionData() {
            if (this.signers.length == 0) return;

            const regionCode = this.signers[0].region_code;
            try {
                const response = await fetch(`/api-py/dict-region-by-code/${regionCode}`);
                   
                if (response.ok) {
                    const result = await response.json();
                    if (result.length > 0) {
                        if (result.length > 1) {
                            this.sortByEndDate(result);
                        }
                        this.region = result[0];
                    }
                } else {
                    this.makeToast('danger', 'Error', 'getRegionByCodeGu error');
                }
            } catch (error) {
                this.makeToast('danger', 'Error', error.toString());
            }
        },

        async getRGuData() {
            if (!('gu' in this.params)) return;

            const guCode = this.params.gu;
            try {
                const response = await fetch(`/api-py/dict-gu-by-code/${guCode}`);
                if (response.ok) {
                    const result = await response.json();
                    if (result.length > 0) {
                        if (result.length > 1) {
                            this.sortByEndDate(result);
                        }
                        this.guDict = result[0];
                    }
                } else {
                    this.makeToast('danger', 'Error', 'get dict gu error');
                }
            } catch (error) {
                this.makeToast('danger', 'Error', error.toString());
            }
        },

        sortByEndDate(arr) {
            arr.sort((a, b) => {
                if (!a.end_date && !b.end_date) return 0;
                if (!a.end_date) return -1;
                if (!b.end_date) return 1;
                return b.end_date.localeCompare(a.end_date);
            });
        },

        makeToast(variant, title, tostbody) {
            this.$bvToast.toast(tostbody, {
                title: title,
                variant: variant,
                toaster: 'b-toaster-top-center',
                autoHideDelay: 5000,
                appendToast: true
            });
        }, // сообщение

        parseQueryParam(value, type = 'string') {
            if (value == null) return null;

            switch (type) {
                case 'number':
                    return Number(value);
                case 'boolean':
                    return value === 'true' || value === '1';
                case 'array':
                    return value.split(',');
                case 'number[]':
                    return value.split(',').map(Number);
                case 'json':
                    try {
                        return JSON.parse(value);
                    } catch (e) {
                        return null;
                    }
                case 'obj':
                    const fixed = value.replace(/'/g, '"').replace('None', null);
                    return JSON.parse(fixed);
                default:
                return value;
            }
        },

        formatIsoToDateTime(isoString) {
            const [datePart, timePart] = isoString.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes, seconds] = timePart.replace('Z', '').split(':').map(Number);
            const pad = n => n.toString().padStart(2, '0');

            return `${pad(day)}-${pad(month)}-${year} ${pad(hours)}:${pad(minutes)}`;
        },

        async downloadFile(year) {
            const ifDataValid = this.dataValidationBeforDownload();
            if (!ifDataValid) return;
            const fileName = this.getFileName(year);
            
            const url =this.getLinkToDownloadFileService(fileName);

            const response = await fetch(url);
            if (response.ok) {
                const contentType = response.headers.get('content-type') || '';

                if (contentType.includes('application/json')) {
                    const errorData = await response.json();
                    this.makeToast('danger', 'Error', errorData.error);
                    return;
                } else {
                    const result = await response.blob();
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(result);
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                }
            }
        }, // скачать файл

        async downloadPageData() {
            this.isMakingPageToPdf = true;
            const element = document.querySelector('.doc-info');
            this.setStylesBeforeConverToPdf();
            html2pdf()
                .set({ 
                    filename: `${this.getLocText('title')}.pdf`,
                    html2canvas: { scale: 2 },
                    jsPDF: { format: 'a4', orientation: 'portrait' }
                })
                .from(element)
                .save()
                .then(() => {
                    this.removeStylesBeforeConverToPdf();
                    this.isMakingPageToPdf = false;
                });
        },

        setStylesBeforeConverToPdf() {
            const elementsToChange = document.querySelectorAll('.doc-info_change-to-print');
            elementsToChange.forEach(elem => {
                elem.classList.add('doc-info_print-page');
            })
        },

        removeStylesBeforeConverToPdf() {
            const elementsToChange = document.querySelectorAll('.doc-info_change-to-print');
            elementsToChange.forEach(elem => {
                elem.classList.remove('doc-info_print-page');
            })
        },

        async copyToClipboard(year) {
            const ifDataValid = this.dataValidationBeforDownload();
            if (!ifDataValid) return;
            const fileName = this.getFileName(year);
            
            const url = this.getLinkToDownloadFileService(fileName);
            const fullBaseUrl = `${window.location.protocol}//${window.location.host}${url}`;
            try {
                await navigator.clipboard.writeText(fullBaseUrl);
                this.makeToast('success', '', this.getLocText('link_copied'));
            } catch (err) {
            }
        },

        getLinkToDownloadFileService(fileName) {
            const data = {
                level: this.params.level,
                curYear: this.curYear,
                region: this.signers[0].region_code, 
                variant: this.params.variant,
                abp: this.params.abp,
                gu: this.params.gu,
                fileName: fileName
            }
            
            const queryString = new URLSearchParams(data).toString();
            const url = `/api-py/get-signed-files?${queryString}`;
            return url;
        },

        dataValidationBeforDownload() {
            if (!('level' in this.params)) {
                this.makeToast('danger', 'Download error', 'No budget level');
                return false;
            }

            if (!this.curYear) {
                this.makeToast('danger', 'Download error', 'No base year');
                return false;
            }

            if (this.signers.length > 0) {
                const oneSigner = this.signers[0];
                if (!('region_code' in oneSigner)) {
                    this.makeToast('danger', 'Download error', 'No region code');
                    return false;
                }
            }

            if (!('variant' in this.params)) {
                this.makeToast('danger', 'Download error', 'No variant id');
                return false;
            }

            if (!('abp' in this.params)) {
                this.makeToast('danger', 'Download error', 'No abp');
                return false;
            }

            if (!('gu' in this.params)) {
                this.makeToast('danger', 'Download error', 'No gu');
                return false;
            }

            if (!('mode' in this.params)) {
                this.makeToast('danger', 'Download error', 'No mode');
                return false;
            }

            return true;
        },

        getFileName(year) {
            const mode = this.params.mode;
            let fileName = `${year}`;
            let hashRecordId1 = 'NULL';
            if ('brft' in mode && mode.brft && mode.brft !== 'None') {
                hashRecordId1 = mode.brft;
            }
            let hashRecordId2 = 'NULL';
            if ('bip_br' in mode && mode.bip_br && mode.bip_br !== 'None') {
                hashRecordId2 = mode.bip_br;
            }
            fileName += `_${hashRecordId1}_${hashRecordId2}.pdf`;
            return fileName;
        },

        onSwitchLangClicked() {
            this.isLangPanelWrapped = !this.isLangPanelWrapped;
        }
    },

    computed: {
        langKz() {
            let lang = 'ru';
            if (this.$i18n.locale === 'kk') lang = 'kz';
            return lang;
        },

        langKk() {
            let lang = 'ru';
            if (this.$i18n.locale === 'kk') lang = 'kk';
            return lang;
        },

        documentType() {
            let type = '';
            if ('level' in this.params) {
                if (this.params.level === "1") type = this.getLocText('budg_concl')
                else type = this.getLocText('budg_request');
            }
            return type;
        },

        curYear() {
            let curYear = null;
            if (this.signers.length > 0) {
                const oneSigner = this.signers[0];
                curYear = oneSigner.year;
            }
            return curYear;
        },

        planPeriod() {
            let planPeriod = '';
            if (this.curYear) {
                planPeriod = `${this.curYear} - ${this.curYear + 2}`;
            }
            return planPeriod;
        },

        years() {
            if (this.curYear) {
                return [this.curYear, this.curYear + 1, this.curYear + 2];
            }
            return [];
        },

        regionName() {
            let regionName = '';
            if (this.region && Object.keys(this.region).length !== 0) {
                regionName = this.region[`name_${this.langKk}`];
            }
            return regionName;
        },

        levelName() {
            let levelName = '';
            if ('level' in this.params) {
                levelName = this.dictLevels[this.params.level][`name_${this.langKk}`];
            }
            return levelName;
        },

        guName() {
            let guName = '';
            if (this.guDict && Object.keys(this.guDict).length !== 0) {
                guName = this.guDict.code + ' - ' + this.guDict[`name_${this.langKk}`];
            }
            return guName;
        },

        variantName() {
            let variantName = '';
            if (this.signers.length > 0) {
                const oneSigner = this.signers[0];
                variantName = oneSigner[`var_name_${this.langKz}`];
            }
            return variantName;
        },

        locales() {
            return this.$store.state.locales;
        }
    }
}

</script>

<style scoped>
    .doc-info_header {
        background: #ffffff;
        height: 60px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        text-align: center;
    }
    .doc-info_header a {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
    }

    .doc-info_header a.doc-info_print-page {
        position: absolute;
        text-align: left;
        left: 5%;
        transform: none;
    }


    .doc-info_header img {
        width: 60%;
    }

    .doc-inf_container {
        margin: auto;
        width: 67%;
        height: calc(100dvh - 60px);
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .doc-inf_container.doc-info_print-page {
        align-items: left;
    }

    .doc-inf_container.doc-info_print-page {
        width: 90%;
    }

    .doc-inf_title {
        text-transform: uppercase;
        font-size: 0.89rem;
        font-weight: 800;
        margin-top: 1rem;
        margin-bottom: 1rem;
        text-align: center;
    }

    .doc-inf_title.doc-info_print-page {
        text-align: left;
    }

    .doc-inf_wrapper {
        width: 100%;
        background: #ffffff;
        height: 50px;
        flex-grow: 1; 
        border-radius: 5px;
        padding: .7rem;
        overflow: auto;
    }

    .doc-inf_section-title {
        text-transform: uppercase;
        font-size: 0.78rem;
        color: #01AC50;
        font-weight: 600;
        margin-left: .3rem;
    }

    .doc-inf_section {
        border: 1px solid #D3DBE3;
        border-radius: 5px;
        padding-top: .5rem;
        padding-right: .8rem;
        padding-bottom: 0;
        padding-left: .8rem;
        margin-bottom: 1rem;
    }

    .doc-inf_section-title.doc-info_print-page {
        display: none;
    }

    .doc-inf_section.doc-info_print-page {
        display: none;
    }

    .doc-inf_section.error {
        background: #F7E8E8;
    }

    .doc-info_row {
        display: flex;
    }

    .doc-info_row p {
        font-size: 0.79em;
        line-height: 1;
        margin-bottom: 15px;
    }

    .doc-info_left-col {
        width: 31%;
    }

    .doc-info_right-col {
        width: 69%;
        flex-grow: 1;
        font-weight: 700; 
    }

    .doc-info_row p.error {
        color: red;
    }
    .doc-info_row span{
        margin-left: 2rem;
    }

    .doc-info_button {
        border: 2px solid #D3DBE3;
        border-radius: 5px;
        color: #6087a0;
        background-color: #EEF1F8;
        text-transform: lowercase;
        font-size: .79rem;
        padding: 0.1rem 0.3rem;
        padding-right: 0.5rem;
        height: 1.3rem;
        cursor: pointer; 
    }

    .doc-info_button:active {
        color: #cbe2f1;
        background-color: #96989f;
    }
    
    .doc-info_copy-icon {
        height: 17px;
    }

    .doc-info_link {
        color: #2196F3;
        text-decoration: underline;
        cursor: pointer;
    }

    .doc-info_link:active {
        color: #6b159d;
    }

    .doc-info_link-wrapper {
        display: flex;
        width: 30%;
    }
 
    .doc-info_row.links {
        min-height: 2rem;
    }

    .doc-info_btn-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .doc-info_btn-wrapper.doc-info_print-page {
        display: none;
    }

    .doc-info_not-valid-container {
        display: flex;
        padding: 2rem 4rem;
        background-color: #f8f8f8;
        border-radius: 1rem;
        box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.3);
    }

    .doc-info_not-valid-container p {
        text-align: center;
        font-size: 1.5rem;
        line-height: 2rem;
        font-weight: 500;
    }

    .doc-info_lang-group {
        margin-left: auto;
        display: flex;
    }

    .doc-info_lang-group.doc-info_print-page {
        display: none;
    }
    
    .doc-info_lang-switch {
        display: none;
    }

    .doc-info_lang-item {
        height: 56px;
        width: 56px;
        padding: 0;
        display: -webkit-inline-box;
        display: -ms-inline-flexbox;
        display: inline-flex;
        -webkit-box-align: center;
        -ms-flex-align: center;
        align-items: center;
        -webkit-box-pack: center;
        -ms-flex-pack: center;
        justify-content: center;
        border-right: none;
        border-top: none;
        border-left: 1px solid #E2E5EB;
        border-bottom: none;
        border-right: none;
        background-color: #FFFFFF;
    }
    .doc-info_lang {
        color: #7C9DB3;
        font-size: 0.8888888889rem;
        font-weight: 500;
        text-transform: uppercase;
        padding: 10px;
        border-radius: 5px;
        cursor: pointer;
        transition: 0.1s all ease;
    }

    .doc-info_lang:hover {
        background-color: #EEF1F8;
        color: #3F4D67;
        font-weight: 600;
    }
    .doc-info_lang.active {
        background-color: #EEF1F8;
        color: #3F4D67;
        font-weight: 600;
    }

    .doc-info_lang-switch span {
        color: #3F4D67;
        font-size: 0.8888888889rem;
        font-weight: 600;
        text-transform: uppercase;
        padding: 10px;
        border-radius: 5px;
        cursor: pointer;
        transition: 0.1s all ease;
        background-color: #EEF1F8;
    }

    .doc-info_footer-wrapper.active {
        background-color: #005f2c;
    }

    @media (max-width: 767px) {

        .doc-inf_container {
            width: 90%;
        }

        .doc-info_row {
            display: block;
        }

        .doc-info_row.doc-info_print-page {
            display: flex;
        }

        .doc-info_row.links {
            display: flex;
        }

        .doc-info_left-col {
            width: 100%;
            color: #6087a0;
        }
        .doc-info_right-col {
            width: 100%;
            font-weight: 500;
        }

        .doc-info_left-col.doc-info_print-page {
            width: 31%;
            color: black;
        }
        .doc-info_right-col.doc-info_print-page {
            width: 69%;
            flex-grow: 1;
            font-weight: 700;
            color: black;
        }

        .doc-info_row p.error span{
            display: block;
            margin-left: 0;
        }

        .doc-info_link-wrapper {
            display: block;
        }

        .doc-info_link {
            white-space: nowrap;
            overflow: visible;
            padding-left: 3px;
            padding-right: 3px;
        }

        .doc-info_footer-wrapper {
            height: 40px;
            text-align: left;
        }

        /* .doc-info_lang-item_ru {
            position: relative;
            z-index: 980;
        }

        .doc-info_lang-item_kk {
            position: relative;
            left: 56px;
            z-index: 970;
        }

        .doc-info_lang-item_en {
            position: relative;
            left: 112px;
            z-index: 960;
        } */
        .list-group-item.active {
            background-color: none;
        }
        .doc-info_lang-item {
            border: 1px solid #E2E5EB;
        }

        .doc-info_lang-group  {
            position: absolute;
            flex-direction: column;
            top: 60px;
            right: 0;
        }

        .doc-info_lang-group.wrapped {
            display: none;
        }

        .doc-info_lang-group.doc-info_print-page {
            display: none;
        }

        .doc-info_lang-switch {
            height: 56px;
            width: 56px;
            text-align: center;
            line-height: 60px;
            display: block;
            position: absolute;
            right: 0px;
            border-left: 1px solid #E2E5EB;
        }

        .doc-info_lang-switch.doc-info_print-page {
            display: none;
        }
    }

    /* Принудительное отображение десктопа */
    body.force-desktop .desktop-only {
        display: block !important;
    }

    body.force-desktop .mobile-only {
        display: none !important;
    }
    
</style>
